import {useCallback, useState} from 'react';

/**
 * Owner side of the `subEditActive` pattern shared by every editor card
 * (TaskItem, ContactCard, CaseCard, LeadCard, CampaignCard, InvoiceCard, …).
 *
 * A card embeds child editors (DocumentBlock, EditableTable-based blocks, etc.)
 * and must know whether one of them currently has an inline editor open, so its
 * OK/Cancel close-guard can refuse to save/close the parent while a child's
 * unsaved draft is open (otherwise the parent's cleanup wipes the draft).
 *
 * The counter is fed by child callbacks: `onBeginEdit` on open, `onEndEdit` on
 * close. Two invariants make it safe:
 *
 *   1. NEVER persisted. The count is transient UI state about what is on screen
 *      RIGHT NOW. Caching it (componentCache / sessionStorage) is the classic
 *      footgun — see OWA `1baee255f`: a child torn down without firing onEndEdit
 *      (ContentBlock viewport/tab teardown, ErrorBoundary swallow, collapse)
 *      leaves the count > 0, and if it was cached the next mount restores the
 *      stale value, stranding the card "save or close the document" with no
 *      editor on screen, FOREVER. Always start at 0 and let children re-signal.
 *
 *   2. Clamped at 0. A duplicate/late onEndEdit can never drive it negative and
 *      mask a genuinely-open editor.
 *
 * Contributors should use {@link useEditContribution} so each child contributes
 * exactly one unit and unwinds it on unmount — together the two hooks keep the
 * count an accurate reflection of "editors actually open".
 */
export interface SubEditActive {
    /** Number of child inline editors currently open. 0 when the card is idle. */
    subEditActive: number;
    /** Whether any child editor is open — the value the close-guard checks. */
    isSubEditing: boolean;
    /** Pass to a child's onBeforeEdit / onBeginEdit / onBeforeAdd. */
    onBeginEdit: () => void;
    /** Pass to a child's onEndEdit / onAfterAdd. */
    onEndEdit: () => void;
}

export default function useSubEditActive(): SubEditActive {
    // Deliberately a plain useState, NOT useComponentCache — see invariant 1.
    const [subEditActive, setSubEditActive] = useState<number>(0);

    const onBeginEdit = useCallback(() => {
        setSubEditActive(prev => prev + 1);
    }, []);

    const onEndEdit = useCallback(() => {
        // Clamp at 0 (invariant 2): a stray decrement can't go negative.
        setSubEditActive(prev => Math.max(0, prev - 1));
    }, []);

    return {subEditActive, isSubEditing: subEditActive > 0, onBeginEdit, onEndEdit};
}
